import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  FileUpload,
  SpaceBetween,
  Container,
  FlashbarProps,
  Flashbar,
} from '@amzn/awsui-components-react';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Button from '@amzn/awsui-components-react/polaris/button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  checkParsedAmbassadorsValidity,
  csvItemsToAmbassadorRequest,
  parseCsvFile,
} from '../../utils/ambassadorCSVParser';
import { useBulkCreateAmbassador } from '../../hooks/ambassadors/ambassadorCreation/createAmbassadorMutation';
import { BulkUpsertAmbassadorsCommandOutput, UpsertAmbassadorRequest } from '@amzn/red-velvet-api';
import { publishKatalMetric } from '../katalAnalytics';

// Max file size is 5MB
const MAX_CSV_FILE_SIZE = 5000000;

interface AmbassadorBulkUpdateFormProps {
  setUploadResult: Dispatch<SetStateAction<BulkUpsertAmbassadorsCommandOutput | undefined>>;
}

export function AmbassadorBulkUpdateForm({ setUploadResult }: AmbassadorBulkUpdateFormProps) {
  const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(null);
  const [fileErrors, setFileErrors] = React.useState<(string | null)[] | undefined>(undefined);
  const [flashbars, setFlashbars] = useState<FlashbarProps.MessageDefinition[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation(['ambassadors', 'translation']);
  const bulkCreateAmbassador = useBulkCreateAmbassador();

  useEffect(() => {
    if (selectedFiles?.length && selectedFiles[0].size > MAX_CSV_FILE_SIZE) {
      setFileErrors([t('bulkUpdate.ambassadors.CSVSizeError')]);
    } else {
      setFileErrors(undefined);
    }
  }, [selectedFiles, setFileErrors, t]);

  const invalidCSVFlashbar = {
    type: 'error',
    dismissible: true,
    dismissLabel: t('ambassadorDirectory.invalidCSV'),
    onDismiss: () => setFlashbars([]),
    content: <>{t('ambassadorDirectory.CSVIsInvalid')}</>,
    id: 'message_1',
  } as FlashbarProps.MessageDefinition;

  const validFilesSelected =
    selectedFiles !== null &&
    !selectedFiles.find((file) => !file.name.endsWith('.csv')) &&
    (!fileErrors || !fileErrors.length);

  const onSubmit = async () => {
    if (selectedFiles && selectedFiles.length > 0) {
      const filename = selectedFiles[0].name;
      const parsedData = await parseCsvFile(selectedFiles[0]);
      let dataAsAmbassadors: UpsertAmbassadorRequest[] = [];
      try {
        dataAsAmbassadors = csvItemsToAmbassadorRequest(parsedData);
        const parsedDataValid = checkParsedAmbassadorsValidity(dataAsAmbassadors);

        if (!parsedDataValid) {
          // CSV Invalid, dont submit and show error
          setFlashbars([invalidCSVFlashbar]);
          console.error('CSV not valid');
        } else {
          // CSV is valid, hide errors and submit
          setFlashbars([]);
          bulkCreateAmbassador.mutate(
            { upsertRequests: dataAsAmbassadors },
            {
              onSuccess: (data) => {
                publishKatalMetric(
                  'bulk_update_ambassador_success',
                  JSON.stringify({ ...dataAsAmbassadors })
                );

                setUploadResult(data.result);
                setFileErrors(undefined);
                navigate(`/ambassadors/bulk-update?filename=${encodeURIComponent(filename)}`);
              },
              onError: (e) => {
                publishKatalMetric(
                  'bulk_update_ambassador_error',
                  JSON.stringify({ ...dataAsAmbassadors, error: e?.message })
                );
                setFileErrors([`${t('bulkUpdate.ambassadors.otherError', { error: e })}`]);
                console.error(e);
              },
            }
          );
        }
      } catch (e) {
        setFlashbars([invalidCSVFlashbar]);
        console.error('CSV not valid', e);
      }
    }
  };

  return (
    <>
      <SpaceBetween direction="vertical" size="m">
        <Flashbar items={flashbars} />
        <Container>
          <SpaceBetween direction="vertical" size="m">
            <FormField label={t('bulkUpdate.ambassadors.uploadCSV')}>
              <FileUpload
                onChange={({ detail }) => setSelectedFiles(detail.value)}
                multiple={false}
                value={selectedFiles || []}
                fileErrors={fileErrors}
                accept=".csv"
                showFileLastModified
                showFileSize
                showFileThumbnail
                i18nStrings={{
                  uploadButtonText: (e) => t('bulkUpdate.ambassadors.chooseFiles'),
                  dropzoneText: (e) => t('bulkUpdate.dropFile'),
                  removeFileAriaLabel: (e) => t('bulkUpdate.removeFile'),
                  limitShowFewer: t('bulkUpdate.showFewer'),
                  limitShowMore: t('bulkUpdate.showMore'),
                  errorIconAriaLabel: t('bulkUpdate.error'),
                }}
              />
              <div style={{ color: '#656871' }}>
                <div>{t('bulkUpdate.ambassadors.onlyCSV')}</div>
                <div>{t('bulkUpdate.ambassadors.CSVSize')}</div>
              </div>
            </FormField>
          </SpaceBetween>
        </Container>
        <div style={{ float: 'right' }}>
          <Button
            variant="primary"
            disabled={!validFilesSelected}
            loading={bulkCreateAmbassador.isPending}
            onClick={onSubmit}
          >
            {t('bulkUpdate.ambassadors.next')}
          </Button>
        </div>
      </SpaceBetween>
    </>
  );
}
